/**
 * Route components that arrive in their own chunks.
 *
 * Separate from `router.tsx` because that file exports the route table, and a
 * module holding both components and plain values loses Fast Refresh for
 * everything in it.
 *
 * One `lazy()` per screen rather than one bundle for the group: a signed-in
 * instructor should never download the registration form, and a visitor on the
 * login page should not download the onboarding wizard.
 */
import { lazy, Suspense } from 'react'
import { useTranslation } from 'react-i18next'

import { LoadingPanel } from '@/shared/ui'

export const AuthLayout = lazy(() =>
  import('@/shared/auth/screens/AuthLayout').then((module) => ({ default: module.AuthLayout })),
)
export const LoginScreen = lazy(() => import('@/shared/auth/screens/LoginScreen'))
export const RegisterScreen = lazy(() => import('@/shared/auth/screens/RegisterScreen'))
export const VerifyEmailScreen = lazy(() => import('@/shared/auth/screens/VerifyEmailScreen'))
export const ForgotPasswordScreen = lazy(() => import('@/shared/auth/screens/ForgotPasswordScreen'))
export const ResetPasswordScreen = lazy(() => import('@/shared/auth/screens/ResetPasswordScreen'))
/**
 * The two email-change link landings — roadmap 18.2, Task 8. Unauthenticated,
 * like `VerifyEmailScreen` above: a click from any device must work, which is
 * the whole value of the old-address notice this pair exists for.
 */
export const EmailChangeConfirmScreen = lazy(
  () => import('@/shared/auth/screens/EmailChangeConfirmScreen'),
)
export const EmailChangeCancelScreen = lazy(
  () => import('@/shared/auth/screens/EmailChangeCancelScreen'),
)
export const OnboardingScreen = lazy(() => import('@/shared/onboarding/OnboardingScreen'))
/**
 * The public course page. Its own chunk, and reachable without signing in —
 * this is the page a shared link opens, so it must not pull the rest of the
 * learner surface down with it (§3.3, NFR-18).
 */
export const CoursePage = lazy(() => import('@/portals/learner/CoursePage'))
/**
 * The education announcement — roadmap 9.5. Public, and the only education
 * surface reachable while the product is switched off.
 */
export const EducationAnnouncement = lazy(() => import('@/portals/learner/EducationAnnouncement'))
/**
 * The public profile at `/u/:handle` — roadmap 11.5. Its own chunk and
 * reachable without signing in: this is what a shared link opens.
 */
export const PublicProfilePage = lazy(() => import('@/portals/learner/PublicProfilePage'))
/**
 * The public listing page at `/listings/:id` — roadmap 14.2. Its own chunk
 * and reachable without signing in: this is the page a shared link opens, and
 * Django has already injected its `<head>` by the time React boots.
 */
export const ListingDetailScreen = lazy(() => import('@/portals/marketplace/ListingDetailScreen'))
/** The profile editor, behind the profile menu. */
export const ProfileEditor = lazy(() => import('@/portals/learner/ProfileEditor'))
/**
 * The notification preference centre at `/me/notifications` — roadmap 17.5.
 *
 * ⚠️ **This path is fixed, not chosen.** `email.PREFERENCES_PATH` has been
 * putting it in every email footer since Task 1 and mail already in inboxes
 * cannot be re-pointed, so renaming the route breaks a link the platform has
 * already sent.
 */
export const NotificationSettings = lazy(() => import('@/portals/learner/NotificationSettings'))
// Roadmap 18.2 — the account screen. Every API it uses shipped in Phase 2 and
// had no UI until now.
export const AccountScreen = lazy(() => import('@/portals/learner/AccountScreen'))
/**
 * The owner's marketplace surfaces — roadmap 14b.
 *
 * Under `/me/` rather than a portal of their own: an owner is any account
 * holder, not a scoped role, so the Marketplace adds no sixth surface. Each is
 * its own chunk — someone who never lists anything should not download the
 * wizard.
 */
export const MyListingsScreen = lazy(() => import('@/portals/marketplace/MyListingsScreen'))
/**
 * The seeker's half of the enquiry flow — what this account has asked for.
 * Distinct from the owner's inbox, which answers who asked about *their*
 * listing; these are different filters over one table.
 */
export const MyEnquiriesScreen = lazy(() => import('@/portals/marketplace/MyEnquiriesScreen'))
export const ListingWizard = lazy(() => import('@/portals/marketplace/ListingWizard'))
export const EnquiryInbox = lazy(() => import('@/portals/marketplace/EnquiryInbox'))
/**
 * The public marketplace browse screen at `/listings` — roadmap 14.1. Public
 * and its own chunk: this is the catalogue a stranger lands on, and it must not
 * pull the owner surface (`/me/listings/`) down with it.
 */
export const BrowseScreen = lazy(() => import('@/portals/marketplace/BrowseScreen'))
/**
 * The public events calendar at `/events` — roadmap 15.8. Public and its own
 * chunk, for the same reason the marketplace catalogue is: a stranger lands
 * here from a shared link and must not download the organizer portal with it.
 */
export const EventBrowseScreen = lazy(() => import('@/portals/learner/events/EventBrowseScreen'))
/** The public event page at `/events/:id` — what a shared link opens. */
export const EventDetailScreen = lazy(() => import('@/portals/learner/events/EventDetailScreen'))
/**
 * The attendee's own admissions at `/me/tickets` — behind `RequireAuth`, and
 * deliberately a separate chunk from the two public screens above: most people
 * who browse the calendar never register.
 */
export const MyTicketsScreen = lazy(() => import('@/portals/learner/events/MyTicketsScreen'))
/**
 * The checkout screen at `/checkout/:orderId` — roadmap 16.6. Not a sixth
 * portal (§8.4): a buyer is any account holder, so this sits beside
 * `AccountScreen` rather than under a portal of its own. Its own chunk —
 * most visits to `/` never reach a payment.
 */
export const CheckoutScreen = lazy(() => import('@/portals/learner/CheckoutScreen'))
/**
 * The buyer's own order history at `/me/orders` — roadmap 16.10. Behind
 * `RequireAuth` and nothing more, like `AccountScreen` and `CheckoutScreen`
 * beside it — a buyer is any account holder, not a scoped role.
 */
export const OrdersScreen = lazy(() => import('@/portals/learner/OrdersScreen'))

/** Suspense boundary with the shared loading label. */
export function Lazy({ children }: { children: React.ReactNode }) {
  const { t } = useTranslation('common')
  return <Suspense fallback={<LoadingPanel label={t('state.loading')} />}>{children}</Suspense>
}
